import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Observable, firstValueFrom, from, timeout } from 'rxjs';
import { AuthService } from '../../features/auth/providers/auth.service';
import { AuthActions, authFeature } from '../../features/auth/state';
import { AuthSessionSyncService } from './auth-session-sync.service';

export const REFRESH_TIMEOUT_MS = 15_000;

export type RenewalOutcome =
  | { status: 'renewed'; token: string }
  | { status: 'session-ended' }
  | { status: 'transient' };

@Injectable({ providedIn: 'root' })
export class TokenRefreshService {
  private readonly store = inject(Store);
  private readonly router = inject(Router);
  private readonly authService = inject(AuthService);
  private readonly sessionSync = inject(AuthSessionSyncService);
  private readonly token = this.store.selectSignal(authFeature.selectToken);
  private readonly refreshToken = this.store.selectSignal(authFeature.selectRefreshToken);
  private inFlight: Promise<RenewalOutcome> | null = null;

  renew(): Observable<RenewalOutcome> {
    if (!this.inFlight) {
      this.inFlight = this.runRenewal().finally(() => (this.inFlight = null));
    }

    return from(this.inFlight);
  }

  endSession(): void {
    if (!this.token()) {
      return;
    }

    this.store.dispatch(AuthActions.sessionExpired({ returnUrl: this.currentUrl() }));
  }

  private currentUrl(): string {
    const navigation = this.router.currentNavigation();

    return (
      (navigation ? this.router.serializeUrl(navigation.extractedUrl) : this.router.url) || '/'
    );
  }

  private async runRenewal(): Promise<RenewalOutcome> {
    const sentRefreshToken =
      this.refreshToken() ?? this.sessionSync.getSessionValue('auth_refreshToken');

    if (!sentRefreshToken) {
      this.endSession();
      return { status: 'session-ended' };
    }

    try {
      const response = await firstValueFrom(
        this.authService.refresh(sentRefreshToken).pipe(timeout(REFRESH_TIMEOUT_MS)),
      );

      if (!response?.token) {
        return { status: 'transient' };
      }

      this.store.dispatch(
        AuthActions.tokensRenewed({
          token: response.token,
          refreshToken: response.refreshToken ?? sentRefreshToken,
        }),
      );

      return { status: 'renewed', token: response.token };
    } catch (error) {
      if (error instanceof HttpErrorResponse && error.status === 401) {
        this.endSession();
        return { status: 'session-ended' };
      }

      return { status: 'transient' };
    }
  }
}
